import { isLegacyBrokenSafeUser } from '@/constants/legacy-broken-safes'
import {
	PUSD_CONTRACT_ADDRESS,
	PUSD_DECIMALS,
	USDC_E_CONTRACT_ADDRESS,
	USDC_E_DECIMALS,
} from '@/constants/tokens'
import { CHAIN_CONFIG } from '@/lib/api/relay'
import { deriveDepositWalletAddress } from '@/lib/wallets/polymarket-deposit'
import { readViemErc20Balance } from '../evm'
import type { MarketBalance, MarketWalletProvider } from '../types'

export const polymarketWalletProvider: MarketWalletProvider = {
	id: 'polymarket',
	label: 'Polymarket',
	direction: 'toPolymarket',
	decimals: PUSD_DECIMALS,
	chain: { chainId: CHAIN_CONFIG.polygon.id, currency: CHAIN_CONFIG.polygon.usdc },
	cashToken: {
		symbol: 'pUSD',
		address: PUSD_CONTRACT_ADDRESS,
		decimals: PUSD_DECIMALS,
	},
	resolveAddress: runtime => {
		if (runtime.addressOverrides?.polymarket) {
			return runtime.addressOverrides.polymarket
		}
		if (!runtime.eoaAddress) return undefined
		// Broken-Safe users always route to deposit wallet, even when the runtime
		// carries a non-null polymarketSafe (e.g. tip/lifi-deposit recipient lookup).
		if (isLegacyBrokenSafeUser(runtime.userId)) {
			return deriveDepositWalletAddress(runtime.eoaAddress)
		}
		if (runtime.polymarketSafe && runtime.polymarketActiveWallet !== 'deposit') {
			return runtime.polymarketSafe
		}
		return deriveDepositWalletAddress(runtime.eoaAddress)
	},
	balanceQueryKey: address => ['market-balance', 'polymarket', address] as const,
	async readBalance(runtime): Promise<MarketBalance | null> {
		const address = this.resolveAddress(runtime)
		if (!(address && runtime.publicClient)) return null

		const [cash, legacyUsdcE] = await Promise.all([
			readViemErc20Balance({
				client: runtime.publicClient,
				tokenAddress: PUSD_CONTRACT_ADDRESS,
				account: address as `0x${string}`,
				decimals: PUSD_DECIMALS,
				symbol: 'pUSD',
			}),
			readViemErc20Balance({
				client: runtime.publicClient,
				tokenAddress: USDC_E_CONTRACT_ADDRESS,
				account: address as `0x${string}`,
				decimals: USDC_E_DECIMALS,
				symbol: 'USDC.e',
			}),
		])

		return {
			platform: 'polymarket',
			address,
			cash,
			secondary: { legacyUsdcE },
		}
	},
}
